import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { Trash, Minus, Plus, ArrowRight, ShoppingBag } from "@phosphor-icons/react";
import { Separator } from "../components/ui/separator";
import API from "../lib/api";

export default function CartPage() {
  const { cart, updateCartItem, removeFromCart } = useCart();
  const { user } = useAuth();
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    if (!cart.total) { setQuote(null); return; }
    API.get("/shipping/quote", { params: { subtotal: cart.total } })
      .then(r => setQuote(r.data))
      .catch(() => setQuote(null));
  }, [cart.total]);

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen pt-28 pb-24 px-6 lg:px-12 max-w-7xl mx-auto flex flex-col items-center justify-center" data-testid="cart-page-empty">
        <ShoppingBag size={48} weight="thin" className="text-[#A1A1AA] mb-6" />
        <h1 className="font-['Cormorant_Garamond'] text-3xl font-light text-[#FDFBF7] mb-4">Your Bag is Empty</h1>
        <p className="text-[#A1A1AA] text-sm font-light mb-8">Discover our luxury collection and find something extraordinary.</p>
        <Link
          to="/shop"
          className="bg-[#D4AF37] text-black px-10 py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#E5C158] transition-colors duration-300"
          data-testid="continue-shopping-btn"
        >
          Continue Shopping
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-24 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="cart-page">
      <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3 font-medium">Your Selection</p>
      <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[#FDFBF7] tracking-tighter mb-12">
        Shopping Bag
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
        {/* Items */}
        <div className="lg:col-span-2 space-y-0">
          {cart.items.map((item, idx) => (
            <div key={item.product_id} data-testid={`cart-line-${item.product_id}`}>
              <div className="flex gap-6 py-8">
                <div className="w-28 h-28 flex-shrink-0 overflow-hidden bg-[#0a0a0a] border border-[rgba(212,175,55,0.1)]">
                  <img src={item.product?.image_url} alt={item.product?.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${item.product_id}`} className="font-['Cormorant_Garamond'] text-xl font-light text-[#FDFBF7] hover:text-[#D4AF37] transition-colors duration-300">
                    {item.product?.name}
                  </Link>
                  <p className="text-xs text-[#A1A1AA] uppercase tracking-[0.15em] mt-1 font-light">
                    {item.product?.category?.replace("-", " ")}
                  </p>
                  <p className="text-[#D4AF37] text-sm mt-2 font-light">${item.product?.price?.toFixed(2)}</p>
                  <div className="flex items-center gap-6 mt-4">
                    <div className="flex items-center border border-white/10">
                      <button onClick={() => updateCartItem(item.product_id, item.quantity - 1)} className="px-3 py-1.5 text-[#A1A1AA] hover:text-[#FDFBF7]" data-testid={`cart-qty-dec-${item.product_id}`}>
                        <Minus size={12} weight="thin" />
                      </button>
                      <span className="px-3 text-sm text-[#FDFBF7] font-light">{item.quantity}</span>
                      <button onClick={() => updateCartItem(item.product_id, item.quantity + 1)} className="px-3 py-1.5 text-[#A1A1AA] hover:text-[#FDFBF7]" data-testid={`cart-qty-inc-${item.product_id}`}>
                        <Plus size={12} weight="thin" />
                      </button>
                    </div>
                    <button onClick={() => removeFromCart(item.product_id)} className="text-[#A1A1AA] hover:text-red-400 transition-colors" data-testid={`cart-remove-${item.product_id}`}>
                      <Trash size={16} weight="thin" />
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[#FDFBF7] font-['Cormorant_Garamond'] text-lg font-light">
                    ${(item.product?.price * item.quantity).toFixed(2)}
                  </p>
                </div>
              </div>
              {idx < cart.items.length - 1 && <Separator className="bg-white/5" />}
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <div className="bg-[#121212] border border-[rgba(212,175,55,0.1)] p-8 sticky top-28" data-testid="cart-summary">
            <h3 className="font-['Cormorant_Garamond'] text-xl font-light text-[#FDFBF7] mb-8">Order Summary</h3>
            <div className="space-y-4 mb-8">
              <div className="flex justify-between text-sm">
                <span className="text-[#A1A1AA] font-light">Subtotal</span>
                <span className="text-[#FDFBF7] font-light">${cart.total?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#A1A1AA] font-light">Shipping</span>
                <span className={quote && quote.shipping === 0 ? "text-[#D4AF37] font-light" : "text-[#FDFBF7] font-light"} data-testid="cart-shipping-value">
                  {quote ? (quote.shipping === 0 ? "Free" : `$${quote.shipping.toFixed(2)}`) : "—"}
                </span>
              </div>
              {quote && quote.amount_to_free_shipping > 0 && (
                <p className="text-xs text-[#A1A1AA] font-light" data-testid="cart-free-shipping-nudge">
                  Add <span className="text-[#D4AF37]">${quote.amount_to_free_shipping.toFixed(2)}</span> more for free shipping
                </p>
              )}
              <Separator className="bg-white/5" />
              <div className="flex justify-between">
                <span className="text-sm uppercase tracking-wide text-[#FDFBF7] font-medium">Total</span>
                <span className="text-xl text-[#D4AF37] font-['Cormorant_Garamond'] font-light" data-testid="cart-total-value">
                  ${quote ? quote.total.toFixed(2) : cart.total?.toFixed(2)}
                </span>
              </div>
            </div>
            <Link
              to={user && user !== false ? "/checkout" : "/auth?redirect=checkout"}
              className="btn-luxury block w-full bg-[#D4AF37] text-black text-center py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#E5C158] transition-colors duration-500"
              data-testid="proceed-to-checkout-btn"
            >
              Proceed to Checkout <ArrowRight size={14} className="inline ml-2" />
            </Link>
            <Link
              to="/shop"
              className="block w-full text-center py-3 text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors duration-300 mt-4 font-light"
              data-testid="continue-shopping-link"
            >
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
